import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useToast } from '../contexts/ToastContext';
import { track } from '../utils/analytics';
import './DjLocationControls.scss';

/**
 * Custom library location for a DJ app (serato / traktor / virtualdj —
 * rekordbox has its own exported-XML picker and auto-follows moved
 * libraries, so it's not listed here).
 *
 * Renders INLINE inside the detection card's actions row (next to Re-scan):
 * no wrapper block, no reserved vertical space. `exists` is tri-state —
 * null/undefined means the existence check hasn't finished, and NOTHING
 * renders (no analytics either): the not-found button must never flash
 * while detection is still running.
 *
 * - exists === false: a real "Locate your <App> library…" button — the
 *   dead-end recovery, meant to be seen.
 * - exists === true: a small ALWAYS-VISIBLE "Change location…" link, muted
 *   so it doesn't compete with anything (hover-reveal tested as
 *   undiscoverable — an empty row that grows a control on hover). The
 *   "custom location" tag appears when an override is active; its ×
 *   reverts to auto-detect.
 */
const DIALOGS = {
    serato: { title: 'Choose your _Serato_ folder (or Serato’s Library folder)', properties: ['openDirectory'] },
    traktor: { title: 'Choose your Traktor collection.nml', properties: ['openFile'], filters: [{ name: 'Traktor collection', extensions: ['nml'] }] },
    virtualdj: { title: 'Choose your VirtualDJ database.xml', properties: ['openFile'], filters: [{ name: 'VirtualDJ database', extensions: ['xml'] }] },
};

// dj_library_not_found fires once per app per session — a rate metric for
// the detection dead end, not a render counter.
const _notFoundTracked = new Set();

export default function DjLocationControls({ app, label, exists, onChanged }) {
    const toast = useToast();
    const [override, setOverride] = useState(null);

    useEffect(() => {
        let cancelled = false;
        window.electron.djapp.getLocation?.(app)
            .then((r) => { if (!cancelled) setOverride(r?.override || null); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [app]);

    useEffect(() => {
        // Strictly false — a pending check (null/undefined) is not a miss.
        if (exists === false && !_notFoundTracked.has(app)) {
            _notFoundTracked.add(app);
            track('dj_library_not_found', { app });
        }
    }, [exists, app]);

    if (!DIALOGS[app] || exists == null) return null;

    const choose = async () => {
        try {
            const dlg = await window.electron.dialog.showOpenDialog(DIALOGS[app]);
            if (dlg.canceled || !dlg.filePaths?.length) return;
            const res = await window.electron.djapp.setLocation(app, dlg.filePaths[0]);
            track('dj_location_changed', { app, action: 'set', valid: !!res?.ok });
            if (!res?.ok) {
                toast.warning(res?.reason || 'Couldn’t use that location', 8000);
                return;
            }
            setOverride(res.path);
            onChanged?.();
        } catch (err) {
            toast.error(`Couldn’t set the ${label} location`);
        }
    };

    const revert = async () => {
        try {
            await window.electron.djapp.setLocation(app, null);
            track('dj_location_changed', { app, action: 'reset' });
            setOverride(null);
            onChanged?.();
        } catch { /* keep current state */ }
    };

    if (!exists) {
        return (
            <>
                <Button variant="primary" size="sm" className="dj-location-locate" onClick={choose}
                    title={`Point MixGoat at your ${label} library if it's in a nonstandard place`}>
                    Locate your {label} library…
                </Button>
                {override && (
                    <span className="dj-location-custom-tag" title={override}>
                        custom location
                        <button type="button" className="dj-location-clear" title="Back to the automatically detected library" onClick={revert}>&times;</button>
                    </span>
                )}
            </>
        );
    }

    return (
        <>
            {override && (
                <span className="dj-location-custom-tag" title={override}>
                    custom location
                    <button type="button" className="dj-location-clear" title="Back to the automatically detected library" onClick={revert}>&times;</button>
                </span>
            )}
            <button type="button" className="dj-location-change" onClick={choose}
                title={`Use a different ${label} library — if MixGoat picked the wrong one`}>
                Change location…
            </button>
        </>
    );
}
